import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProductCarousel from "../../components/ProductCarousel/ProductCarousel";
import { ProductWithoutIndex } from "../../interfaces/interfaces";
import { log } from "../../commonfunctions/logger";
import { getSubdomain } from "../../context/ContextApp";
import { useNavigate } from "react-router-dom";
import { subdomainTypes } from "../../context/availableSubdomains";
import { updateCurrentOrder } from "../../redux/actions/orders";
import { useDispatch } from "react-redux";
import getProducts from "./fetch/getProducts";
import getSliders, { SliderDetailResponse } from "./fetch/getSliders";
import getCurrentOrder from "./fetch/getCurrentOrder";
import { homePageBrands } from "./data";

const HomePage = () => {
  const [sliderResponse, setSliderResponse] = useState<SliderDetailResponse | null>(null);
  const [products, setProducts] = useState<ProductWithoutIndex[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [clearanceProducts, setClearanceProducts] = useState<ProductWithoutIndex[]>([]);
  const [loadingClearanceProducts, setLoadingClearanceProducts] = useState<boolean>(false);
  const [loadingSliders, setLoadingSliders] = useState<boolean>(false);
  const appBranch = getSubdomain();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchSliders = async () => {
      setLoadingSliders(true);
      try {
        const data = await getSliders(appBranch);
        if (data) setSliderResponse(data);
      } catch (error) {
        log(error, "Failed to load sliders");
      } finally {
        setLoadingSliders(false);
      }
    };

    const fetchProducts = async () => {
      setLoadingProducts(true);
      const result = await getProducts(appBranch);
      if (result.products) setProducts(result.products);
      setLoadingProducts(false);
    };

    const fetchClearanceProducts = async () => {
      setLoadingClearanceProducts(true);
      const result = await getProducts(appBranch, "&sales_status=clearance");
      if (result.products) setClearanceProducts(result.products);
      setLoadingClearanceProducts(false);
    };

    const fetchCurrentOrder = async () => {
      const result = await getCurrentOrder();
      if (result.order) dispatch(updateCurrentOrder(result.order));
    };

    if (appBranch === subdomainTypes.CA) {
      navigate("/store/cart");
    }

    fetchCurrentOrder();
    fetchSliders();
    fetchProducts();
    fetchClearanceProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderSliderSkeletons = () =>
    Array.from({ length: 3 }).map((_, index) => (
      <div key={`slider-skeleton-${index}`} className="slider-skeleton">
        <div className="slider-skeleton-image" />
      </div>
    ));

  const sublimationWebsite = process.env.REACT_APP_SUBLIMATION_WEBSITE;
  const jwtToken = localStorage.getItem("jwt") ?? "";
  const bizColabUrl = sublimationWebsite ? `${sublimationWebsite}?jwt=${jwtToken}` : null;

  const visibleBrands = useMemo(() => {
    const userBrands = localStorage.getItem("brands") ?? "";
    return homePageBrands
      .filter((brand) => {
        if (brand.brand === "biz-co-lab") return !!bizColabUrl;
        return brand.pinned || userBrands.includes(brand.brand);
      })
      .map((brand) =>
        brand.brand === "biz-co-lab" ? { ...brand, link: bizColabUrl ?? "" } : brand,
      );
  }, [bizColabUrl]);

  return (
    <div className="home-page">
      {visibleBrands.length > 0 && (
        <section className="brand-browse">
          <h2>Order By Brand</h2>
          <div
            className={`brand-browse__grid${visibleBrands.length <= 3 ? " brand-browse__grid--small" : ""}${visibleBrands.length > 6 ? " brand-browse__grid--large" : ""}`}
            style={{ "--brand-count": visibleBrands.length } as React.CSSProperties}
          >
            {visibleBrands.map((brand) => (
              <Link
                key={brand.altName}
                to={brand.link ?? ""}
                className="brand-browse__item"
                {...(brand.isExternal && { target: "_blank", rel: "noreferrer" })}
              >
                <img src={brand.image} alt={brand.altName} className="brand-browse__image" />
              </Link>
            ))}
          </div>
        </section>
      )}
      <ProductCarousel title="Just Landed" products={products} loading={loadingProducts} />
      <ProductCarousel
        title="Clearance"
        products={clearanceProducts}
        loading={loadingClearanceProducts}
        viewAllUrl="/products?sales_status=clearance"
      />
      <div className="slider-grid">
        {loadingSliders ? (
          <section className="slider_section">{renderSliderSkeletons()}</section>
        ) : (
          sliderResponse?.slider?.slice(1).map((slide) => (
            <section key={slide.id} className="slider_section">
              <a href={slide.link} target="_blank" className="slider_card" rel="noreferrer">
                <img alt="slider image" src={slide.image} className="slider-img" />
              </a>
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default HomePage;
